import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import { Shield, Plus, AlertCircle, Eye, Download, Trash2, FileText, AlertTriangle, WifiOff, Cloud, RefreshCw } from 'lucide-react';
import SafetyAuditDialog from '@/components/SafetyAuditDialog';
import ViewAuditDialog from '@/components/ViewAuditDialog';
import { AnalysisProvenanceBadge } from '@/components/AnalysisProvenanceBadge';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { useOffline } from '@/contexts/OfflineContext';
import { getOfflineAudits } from '@/services/syncService';
import { saveBulkToStore } from '@/services/offlineDB';
import { useTranslation } from 'react-i18next';

const SupervisorAudits = ({ user }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const divisionIdFromUrl = searchParams.get('divisionId');
  const effectiveDivisionId = divisionIdFromUrl || user?.divisionId || null;
  
  const [safetyAudits, setSafetyAudits] = useState([]);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [isOfflineData, setIsOfflineData] = useState(false);
  
  // Offline context
  const { isOnline, pendingCount, manualSync, isSyncing } = useOffline();

  const loadOfflineAudits = async () => {
    try {
      const offlineAudits = await getOfflineAudits();
      if (offlineAudits && offlineAudits.length > 0) {
        setSafetyAudits(offlineAudits);
        setIsOfflineData(true);
        toast.info(t('audits.showingCachedAudits'), { duration: 3000 });
      } else {
        setSafetyAudits([]);
        toast.warning(t('audits.noCachedAudits'), { duration: 4000 });
      }
    } catch (error) {
      console.error('Failed to load offline audits:', error);
      setSafetyAudits([]);
    }
  };

  const loadSafetyAudits = async () => {
    setLoading(true);
    
    // Try online first, fall back to offline
    if (isOnline) {
      try {
        const params = effectiveDivisionId ? `?divisionId=${effectiveDivisionId}` : '';
        const response = await axiosInstance.get(`/safety-audits${params}`);
        setSafetyAudits(response.data);
        setIsOfflineData(false);
        
        // Cache audits for offline use
        if (response.data && response.data.length > 0) {
          await saveBulkToStore('audits', response.data);
          console.log('📦 Cached', response.data.length, 'audits for offline');
        }
      } catch (error) {
        console.error('Failed to load safety audits online, trying offline:', error);
        await loadOfflineAudits();
      }
    } else {
      await loadOfflineAudits();
    }
    
    setLoading(false);
  };

  useEffect(() => {
    loadSafetyAudits();
  }, [divisionIdFromUrl, isOnline]); // eslint-disable-line

  const handleCreateAudit = () => {
    setShowAuditDialog(true);
  };

  const handleAuditSuccess = () => {
    loadSafetyAudits();
    setShowAuditDialog(false);
  };

  const handleViewAudit = async (audit) => {
    try {
      // Fetch full audit details
      const response = await axiosInstance.get(`/safety-audits/${audit.id}`);
      setSelectedAudit(response.data);
      setShowViewDialog(true);
    } catch (error) {
      console.error('Failed to load audit details:', error);
      toast.error(t('audits.auditDetails'));
    }
  };

  // A supervisor's list covers their whole division, but detail/export stay scoped to the
  // audits they authored — don't offer buttons the API will refuse.
  const canOpen = (audit) =>
    user?.role !== 'supervisor' || !audit.auditorId || audit.auditorId === user?.id;

  const handleDownloadAuditPDF = async (auditId, format = 'comprehensive') => {
    try {
      const response = await axiosInstance.get(`/safety-audits/${auditId}/export-pdf`, {
        params: { format },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileName = `safety_audit_${format}_${auditId.slice(0, 8)}.pdf`;
      
      // Check if running in Capacitor (mobile app)
      if (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) {
        // Mobile: Use Capacitor Filesystem
        try {
          const { Filesystem, Directory } = await import('@capacitor/filesystem');
          const { Share } = await import('@capacitor/share');
          
          // Convert blob to base64
          const reader = new FileReader();
          reader.onloadend = async () => {
            const base64Data = reader.result.split(',')[1];
            
            // Write file to device
            const result = await Filesystem.writeFile({
              path: fileName,
              data: base64Data,
              directory: Directory.Documents,
            });
            
            // Share/Open the file
            await Share.share({
              title: 'Safety Audit Report',
              text: 'Safety Audit PDF Report',
              url: result.uri,
              dialogTitle: 'Share or Open PDF'
            });
            
            toast.success(t('audits.pdfReady'));
          };
          reader.readAsDataURL(blob);
        } catch (mobileError) {
          console.error('Mobile download error:', mobileError);
          // Fallback to opening in new tab
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          toast.info(t('supervisorPages.pdfOpenedNewTab'));
        }
      } else {
        // Desktop: Standard download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(t('audits.pdfDownloaded'));
      }
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error(t('common.pdfFailed'));
    }
  };

  const handleDeleteAudit = async (auditId, location) => {
    if (!window.confirm(t('audits.auditDeleteConfirm'))) {
      return;
    }
    try {
      await axiosInstance.delete(`/safety-audits/${auditId}`);
      toast.success(t('audits.auditDeleted'));
      loadSafetyAudits();
    } catch (error) {
      console.error('Failed to delete audit:', error);
      toast.error(t('common.deleteFailed'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className="mb-4 hover:bg-gray-100"
        >
          ← Back
        </Button>

        {/* Offline Mode Banner */}
        {!isOnline && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-3">
            <WifiOff className="h-5 w-5 text-orange-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800">{t('audits.offlineMode')}</p>
              <p className="text-xs text-orange-600">{t('audits.offlineMessage')}</p>
            </div>
          </div>
        )}

        {/* Header - mobile responsive */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
              {t('audits.title')}
              {isOfflineData && (
                <span className="inline-flex items-center gap-1 text-sm font-normal text-orange-600 bg-orange-50 px-2 py-1 rounded">
                  <WifiOff className="w-4 h-4" />
                  {t('offline.offlineMode')}
                </span>
              )}
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">{t('audits.subtitle')}</p>
          </div>
          <div className="flex gap-2">
            {pendingCount > 0 && isOnline && (
              <Button
                onClick={manualSync}
                variant="outline"
                disabled={isSyncing}
                className="text-blue-600 border-blue-600 hover:bg-blue-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                {t('audits.sync')} ({pendingCount})
              </Button>
            )}
            <Button onClick={handleCreateAudit} className="bg-green-600 hover:bg-green-700 w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              {t('audits.newAudit')}
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="px-3 sm:px-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              {t('audits.title')} ({t('audits.aiEnhanced')})
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">{t('audits.aiHazardDetection')}</CardDescription>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-green-600 mx-auto"></div>
              </div>
            ) : safetyAudits.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <Shield className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-gray-500 text-base sm:text-lg mb-2">{t('audits.noAudits')}</p>
                <p className="text-xs sm:text-sm text-gray-400 mb-4">{t('audits.createFirstAudit')}</p>
                <Button onClick={handleCreateAudit} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('audits.createAudit')}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {safetyAudits.map(audit => (
                  <div key={audit.id} className="border rounded-lg p-3 sm:p-4 hover:bg-gray-50 transition-colors">
                    {/* Mobile-first responsive layout */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Title and risk badge */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{audit.location}</h3>
                          {audit.calculatedScoring ? (
                            <>
                              <span
                                className={`text-xs font-medium px-2 py-0.5 rounded whitespace-nowrap ${
                                  ['HIGH', 'CRITICAL'].includes(audit.calculatedScoring.risk_level) ? 'bg-red-100 text-red-800' :
                                  audit.calculatedScoring.risk_level === 'MODERATE' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}
                                data-testid={`audit-risk-badge-${audit.id}`}
                              >
                                {t('audits.riskPrefix')} {audit.calculatedScoring.risk_level}
                              </span>
                              <span
                                className="text-xs font-medium px-2 py-0.5 rounded whitespace-nowrap bg-gray-100 text-gray-700"
                                data-testid={`audit-compliance-badge-${audit.id}`}
                              >
                                {audit.calculatedScoring.compliance_rate}{t('audits.complianceSuffix')}
                              </span>
                              {audit.aiAnalysisProvenanceBadge?.hasAnalysis && (
                                <AnalysisProvenanceBadge
                                  badge={audit.aiAnalysisProvenanceBadge}
                                  compact
                                />
                              )}
                            </>
                          ) : (
                            <span
                              className="text-xs font-medium px-2 py-0.5 rounded whitespace-nowrap bg-gray-100 text-gray-600"
                              data-testid={`audit-risk-badge-${audit.id}`}
                            >
                              {t('audits.notScoredYet')}
                            </span>
                          )}
                        </div>
                        {/* Auditor and date */}
                        <p className="text-xs sm:text-sm text-gray-600 mb-2">
                          Auditor: {audit.auditorName} | Date: {audit.auditDate ? new Date(audit.auditDate).toLocaleDateString() : 'N/A'}
                        </p>
                        {/* AI Hazards */}
                        {audit.aiHazards && audit.aiHazards.length > 0 && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm">
                            <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600 flex-shrink-0" />
                            <span className="text-gray-700">{audit.aiHazards.length} hazards detected</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Right side: Score + Actions */}
                      <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2 sm:gap-3">
                        {/* Items Passed - inline on mobile */}
                        <div className="text-left sm:text-right">
                          <p className="text-sm sm:text-base font-medium text-gray-900" data-testid={`audit-passed-count-${audit.id}`}>
                            {audit.calculatedScoring
                              ? `${audit.calculatedScoring.passed_checks} / ${audit.calculatedScoring.applicable_items}`
                              : '0 / 0'}
                            <span className="text-xs text-gray-500 ml-1 sm:hidden">Passed</span>
                          </p>
                          <p className="text-xs text-gray-500 hidden sm:block">{t('common.itemsPassed')}</p>
                        </div>
                        
                        {/* Action buttons - icons only on mobile */}
                        <div className="flex gap-1.5 sm:gap-2">
                          {/* Supervisors may only open audits they authored; the list shows the
                              whole division, so these are hidden on other auditors' audits */}
                          {canOpen(audit) && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewAudit(audit)}
                                className="text-blue-600 hover:text-blue-700 h-8 px-2 sm:px-3"
                                data-testid={`audit-view-${audit.id}`}
                              >
                                <Eye className="w-4 h-4 sm:mr-1" />
                                <span className="hidden sm:inline">{t('audits.viewAudit')}</span>
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600 hover:text-green-700 h-8 px-2 sm:px-3"
                                    data-testid={`audit-pdf-menu-${audit.id}`}
                                  >
                                    <Download className="w-4 h-4 sm:mr-1" />
                                    <span className="hidden sm:inline">PDF</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => handleDownloadAuditPDF(audit.id, 'standard')}
                                    data-testid={`audit-pdf-standard-${audit.id}`}
                                  >
                                    {t('audits.standardReport')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleDownloadAuditPDF(audit.id, 'comprehensive')}
                                    data-testid={`audit-pdf-comprehensive-${audit.id}`}
                                  >
                                    {t('audits.comprehensiveReport')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteAudit(audit.id, audit.location)}
                            className="text-red-600 hover:text-red-700 h-8 px-2 sm:px-3"
                          >
                            <Trash2 className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">Delete</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Safety Audit Dialog */}
      <SafetyAuditDialog
        open={showAuditDialog}
        onOpenChange={setShowAuditDialog}
        user={user}
        onSuccess={handleAuditSuccess}
        divisionIdOverride={effectiveDivisionId}
      />

      {/* View Audit Dialog - Using ViewAuditDialog component with JSA Recommendations */}
      <ViewAuditDialog
        user={user}
        audit={selectedAudit}
        open={showViewDialog}
        onClose={() => {
          setShowViewDialog(false);
          setSelectedAudit(null);
        }}
        onDelete={() => {
          loadSafetyAudits();
          setShowViewDialog(false);
          setSelectedAudit(null);
        }}
      />
    </div>
  );
};

export default SupervisorAudits;